import type { AudioDetection, Event, PushCategory, PushFilters, PushPreferences, WsEventPayload } from '@/types'
import type { MyCameraFilters } from '@/lib/myPush'

/**
 * #139 / CP 163 — the web mirror of the backend's `push_categories` (event_categories.py).
 * `other` is a TRUE RESIDUAL: it applies only when NOTHING else matched, so a vehicle-only
 * event is `{vehicle}` and a sound-only event is `{audio}`. This makes it NARROWER than the
 * events-page 'Other' tab, which still means "neither animal nor human" and DOES include
 * vehicles — which is why the push label is "Motion & objects" and not "Other".
 * Class spellings are matched exactly, lowercase, no space->underscore normalisation
 * (COCO keeps spaces, e.g. 'traffic light').
 */
const PERSON_CLASSES = new Set(['person'])

const VEHICLE_CLASSES = new Set([
  'vehicle', // SpeciesNet generic box (#112)
  'bicycle', 'car', 'motorcycle', 'airplane', 'bus', 'train', 'truck', 'boat', // COCO
])

const OBJECT_CLASSES = new Set([ // COCO inanimate — exact spellings, lowercase, spaces kept
  'traffic light', 'fire hydrant', 'stop sign', 'parking meter', 'bench',
  'backpack', 'umbrella', 'handbag', 'tie', 'suitcase',
  'frisbee', 'skis', 'snowboard', 'sports ball', 'kite', 'baseball bat', 'baseball glove',
  'skateboard', 'surfboard', 'tennis racket',
  'bottle', 'wine glass', 'cup', 'fork', 'knife', 'spoon', 'bowl',
  'banana', 'apple', 'sandwich', 'orange', 'broccoli', 'carrot', 'hot dog', 'pizza', 'donut', 'cake',
  'chair', 'couch', 'potted plant', 'bed', 'dining table', 'toilet',
  'tv', 'laptop', 'mouse', 'remote', 'keyboard', 'cell phone',
  'microwave', 'oven', 'toaster', 'sink', 'refrigerator',
  'book', 'clock', 'vase', 'scissors', 'teddy bear', 'hair drier', 'toothbrush',
])

const NON_ANIMAL_CLASSES = new Set<string>([
  ...PERSON_CLASSES,
  ...VEHICLE_CLASSES,
  ...OBJECT_CLASSES,
])

/**
 * #139 — the shapes shouldRingBell/pushCategoriesOf accept: the full REST `Event`,
 * or the lightweight WS broadcast payload the task handler actually sends
 * (`WsEventPayload`). `WebSocketProvider.tsx` passes the WS payload on every live
 * event; `Event` support exists for defensive/REST-cache callers and for tests.
 */
type BellEvent = Event | WsEventPayload

/** Union of an event's audio class names, whichever shape supplied them. */
function audioClassNamesOf(event: BellEvent): string[] {
  const detections: AudioDetection[] = ('audio_detections' in event ? event.audio_detections : undefined) ?? []
  const classNames: string[] = ('audio_class_names' in event ? event.audio_class_names : undefined) ?? []
  return [...detections.map((d) => d.class_name), ...classNames]
}

/**
 * FALLBACK MIRROR of the backend's `push_categories()` (task handler
 * `core/database.py` / Django `event_categories.py`). Only used when the payload
 * does not already carry the server-authoritative `categories` array — the live WS
 * path always has it, so this only runs for a bare REST `Event` (no `categories`
 * field). If the backend taxonomy changes, this must change too or the two will
 * silently drift.
 *
 * Never empty. "Animal" is the COMPLEMENT of a finite, frozen non-animal set
 * (person + COCO vehicles + COCO inanimate objects) — a newly added BioCLIP catalog
 * species is never a COCO name, so it is an animal automatically and this set never
 * needs editing for the catalog.
 */
export function pushCategoriesOf(event: BellEvent): PushCategory[] {
  const types = (event.detected_objects ?? []).map((t) => t.toLowerCase())
  const cats = new Set<PushCategory>()

  if (types.some((t) => PERSON_CLASSES.has(t))) cats.add('human')
  if (types.some((t) => !NON_ANIMAL_CLASSES.has(t))) cats.add('animal')
  if (types.some((t) => VEHICLE_CLASSES.has(t))) cats.add('vehicle')
  if (audioClassNamesOf(event).length > 0) cats.add('audio')
  if (cats.size === 0) cats.add('other') // CP 163 — a TRUE residual, mirrors event_categories.py

  return [...cats]
}

/**
 * #139 — should this event announce itself in-app (bell, peek card, sound)?
 *
 * This MIRRORS the server's `push_matches` (event_categories.py): an event rings if
 * ANY of its categories is enabled AND that category either carries no class
 * restriction or the event's classes intersect it. A missing category key means
 * enabled (never silently mute a category added after the user last saved). A class
 * inside a DISABLED category can never ring — the #138 union rule this replaces
 * allowed that; killing it is the point of #139.
 *
 * Categories: the server sends authoritative `categories` on every live WS payload
 * (`WsEventPayload.categories`, from the handler's `push_categories()`) — prefer
 * those. Only fall back to the local `pushCategoriesOf()` derivation when they are
 * absent or empty, e.g. a bare REST `Event`. Relying on local derivation for the WS
 * path was the #139 bug: the WS payload has no `audio_detections`, so the `audio`
 * category and audio class names were silently dropped for every real event.
 *
 * Class matching reads audio class names from BOTH shapes — `audio_class_names`
 * (WS payload, `string[]`) and `audio_detections` (REST `Event`,
 * `{class_name}[]`) — unioned into one match set, so a user who subscribed to a
 * specific audio class (e.g. "Gunshot") gets the bell regardless of which shape
 * delivered the event.
 *
 * The WS message itself is NOT filtered server-side: one message invalidates the event
 * list AND the camera list caches, so dropping it for a muted user would leave their
 * dashboard stale. The event always arrives and is always browsable — this only decides
 * whether it is *announced*. FCM push is filtered server-side; this is UX, not access
 * control.
 *
 * Pure (no React/store imports) so it can be tested in isolation.
 */
function matchesFilters(
  names: Set<string>,
  categories: PushCategory[],
  filters: Partial<PushFilters> | undefined,
): boolean {
  for (const cat of categories) { // never empty
    const f = filters?.[cat]
    if (f && !f.enabled) continue // absent => enabled
    const classes = (f?.classes ?? []).map((c) => c.toLowerCase())
    if (classes.length === 0 || classes.some((c) => names.has(c))) return true
  }
  return false
}

export function shouldRingBell(
  event: BellEvent,
  prefs: PushPreferences | undefined,
  cameraFilters?: Record<number, PushFilters>,
  mutedCameraIds?: ReadonlySet<number>,
  myCameraFilters?: MyCameraFilters | null,
): boolean {
  // #337 — one rule: the bell rings exactly when no mute names the event
  return muteReason(event, prefs, cameraFilters, mutedCameraIds, myCameraFilters) === null
}

/** #337 — which push mute silenced an event: the camera's push switch (CP 164), the camera's own categories
 *  (CP 159) or the user's categories (#138). CP 391 — or the person's own choice for this camera ("My phone alerts",
 *  their camera row), which replaces their categories there. */
export type MuteReason = 'camera-off' | 'camera-filter' | 'user-filter' | 'my-camera-choice'

/**
 * #337 — the checks of `shouldRingBell`, in the same order, naming the first one that silenced the event: the
 * Dashboard's Recent events rows show it (the row of a muted event arrives with no sound, and the mark says why).
 * null = not muted, or the preferences are not loaded yet (the bell rings then too).
 *
 * CP 391 — `myCameraFilters`: the person's OWN camera rows ("My phone alerts"), camera id -> the stored filter map
 * exactly as GET /push-preferences/cameras/ returns it (`_mode` marker included). For a camera with a row, that map
 * REPLACES the account filters, as the handler's COALESCE(camera row, account row) does; a camera without one uses
 * the account filters. `null` = that list is still loading: not loaded, like `prefs` (never swallow an alert the
 * phone may have received). `undefined` = the caller has no per-camera rows: the account filters for every camera.
 * The camera's own switch (CP 164) and filter (CP 159) still come first: a row cannot bring back what they drop.
 */
export function muteReason(
  event: BellEvent,
  prefs: PushPreferences | undefined,
  cameraFilters?: Record<number, PushFilters>,
  mutedCameraIds?: ReadonlySet<number>,
  myCameraFilters?: MyCameraFilters | null,
): MuteReason | null {
  if (!prefs) return null // preferences not loaded yet — never swallow an alert
  if (myCameraFilters === null) return null // CP 391 — the per-camera rows are not loaded yet: the same rule

  // CP 164 — the camera's own push switch. The server returns before sending ANY FCM for
  // a camera with push_enabled = false; the bell used to ring anyway, so a camera the
  // owner had muted still announced every event in the app. The WS message still arrives
  // and still refreshes the event and camera caches — only the announcement is dropped.
  const cameraId = (event as { camera_id?: number }).camera_id
  if (cameraId !== undefined && mutedCameraIds?.has(cameraId)) return 'camera-off'

  const names = new Set<string>([
    ...(event.detected_objects ?? []).map((n) => n.toLowerCase()),
    ...audioClassNamesOf(event).map((n) => n.toLowerCase()),
  ])

  const categories =
    'categories' in event && event.categories && event.categories.length > 0
      ? event.categories
      : pushCategoriesOf(event) // fallback: no server categories on this payload

  // CP 159 — the CAMERA's own category filter, ANDed with the recipient's exactly as the
  // server does (camera_filter_blocks runs first and returns before any per-recipient
  // check). An absent or empty filter means "everything", so this is a no-op for every
  // camera that has not been configured — which is all of them at deploy time.
  const cameraFilter = cameraId === undefined ? undefined : cameraFilters?.[cameraId]
  if (cameraFilter && Object.keys(cameraFilter).length > 0
      && !matchesFilters(names, categories, cameraFilter)) {
    return 'camera-filter'
  }

  // CP 391 — the person's own camera row replaces their account filters for this camera. The matcher looks up only
  // the event's own categories, so the row's `_mode` marker never takes part (as in push_matches). A mute by the row
  // is named for it: the account's notification settings did not decide it.
  const ownRow = cameraId === undefined ? undefined : myCameraFilters?.[cameraId]
  // #406 — a suspicious event: its own switch instead of the categories, as the handler's push audience reads it. Only
  // an explicit false on the effective row (camera row over account row) mutes it; an absent key reads as on. The
  // account's API answer has the switch next to `filters`; the stored form inside them is read too.
  if (event.level === 'suspicious') {
    if (ownRow) return ownRow.suspicious === false ? 'my-camera-choice' : null
    return (prefs.suspicious ?? prefs.filters.suspicious) === false ? 'user-filter' : null
  }
  if (ownRow) return matchesFilters(names, categories, ownRow) ? null : 'my-camera-choice'
  return matchesFilters(names, categories, prefs.filters) ? null : 'user-filter'
}

/** #337 — the camera side of the mutes, from the cameras list: each camera's own filter (CP 159; only when it
 *  has one) and the cameras whose push is off (CP 164). Moved from WebSocketProvider (usePushMutes), so the live
 *  alerts and the Dashboard's muted mark read the same derivation. */
export function pushMuteInputsOf(
  cameras: ReadonlyArray<{ id: number; push_enabled?: boolean; push_filters?: PushFilters | null }> | undefined,
): { cameraFilters: Record<number, PushFilters>; mutedCameraIds: ReadonlySet<number> } {
  const cameraFilters: Record<number, PushFilters> = {}
  const mutedCameraIds = new Set<number>()
  for (const cam of cameras ?? []) {
    if (cam.push_filters && Object.keys(cam.push_filters).length > 0) {
      cameraFilters[cam.id] = cam.push_filters
    }
    if (cam.push_enabled === false) mutedCameraIds.add(cam.id)
  }
  return { cameraFilters, mutedCameraIds }
}
